import React from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../hooks/useContent';

const F = "'Inter', system-ui, sans-serif";

export default function Cafe() {
  const { content, loading, error } = useContent();

  if (loading) return <div style={styles.loading}>Inhalte werden geladen...</div>;
  if (error) return <div style={styles.errorBox}>Inhalte konnten nicht geladen werden.<br /><small>{error}</small></div>;
  if (!content) return null;

  const cafe = content.cafe || {};
  const hours = cafe.hours || [];
  const menu = cafe.menu || [];

  return (
    <main style={styles.page}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div style={styles.pageHeaderInner}>
          <span style={styles.eyebrow}>Genuss & Erholung</span>
          <h1 style={styles.heading}>{cafe.heading || 'Unser Café'}</h1>
          <p style={styles.sub}>{cafe.subheading}</p>
        </div>
      </div>

      <div style={styles.inner}>
        {/* Intro */}
        <section style={styles.introSection}>
          <div style={styles.introGrid}>
            <div>
              <h2 style={styles.sectionHeading}>Willkommen im Café Spirkenhof</h2>
              {cafe.intro && <p style={styles.introText}>{cafe.intro}</p>}
              {cafe.intro2 && <p style={styles.introText}>{cafe.intro2}</p>}
            </div>
            <div style={styles.hoursCard}>
              <h3 style={styles.hoursHeading}>
                <span style={styles.hoursIcon}>🕐</span> Öffnungszeiten
              </h3>
              {hours.map(h => (
                <div key={h.id} style={styles.hoursRow}>
                  <span style={styles.hoursDay}>{h.day}</span>
                  <span style={styles.hoursTime}>{h.time}</span>
                </div>
              ))}
              {cafe.seasonNote && <p style={styles.hoursNote}>{cafe.seasonNote}</p>}
            </div>
          </div>
        </section>

        {/* Menu */}
        {menu.length > 0 && (
          <section style={styles.menuSection}>
            <span style={styles.eyebrowDark}>Speisekarte</span>
            <h2 style={styles.sectionHeading}>Was wir anbieten</h2>
            <div style={styles.menuGrid}>
              {menu.map(cat => (
                <div key={cat.id} style={styles.menuCard}>
                  <div style={styles.menuCardHeader}>
                    <span style={styles.menuCatIcon}>{cat.icon}</span>
                    <h3 style={styles.menuCatTitle}>{cat.category}</h3>
                  </div>
                  <div style={styles.menuItems}>
                    {(cat.items || []).map(item => (
                      <div key={item.id} style={styles.menuItem}>
                        <div style={styles.menuItemTop}>
                          <span style={styles.menuItemName}>{item.name}</span>
                          <span style={styles.menuItemPrice}>{item.price}</span>
                        </div>
                        <span style={styles.menuItemDesc}>{item.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p style={styles.menuNote}>Alle Preise inkl. MwSt. · Angebot kann saisonal variieren.</p>
          </section>
        )}

        {/* CTA */}
        <section style={styles.ctaSection}>
          <h2 style={styles.ctaHeading}>Planen Sie Ihren Aufenthalt?</h2>
          <p style={styles.ctaText}>Kommen Sie vorbei – wir freuen uns auf Ihren Besuch!</p>
          <div style={styles.ctaButtons}>
            <Link to="/buchung" style={styles.ctaBtn}>Jetzt buchen</Link>
            <Link to="/galerie" style={styles.ctaBtnSecondary}>Galerie ansehen</Link>
          </div>
        </section>
      </div>
    </main>
  );
}

const styles = {
  page: { backgroundColor: 'white', minHeight: '70vh' },
  loading: { textAlign: 'center', padding: '6rem 2rem', fontFamily: F, color: '#1e8449', fontSize: '1rem' },
  errorBox: { textAlign: 'center', padding: '4rem 2rem', fontFamily: F, color: '#dc3545' },

  pageHeader: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    padding: '4rem 1.5rem 3.5rem',
    textAlign: 'center',
  },
  pageHeaderInner: { maxWidth: '640px', margin: '0 auto' },
  eyebrow: { fontSize: '0.75rem', fontFamily: F, fontWeight: '600', letterSpacing: '1.8px', textTransform: 'uppercase', color: '#1e8449' },
  heading: { color: '#111827', fontFamily: F, fontSize: 'clamp(1.9rem,5vw,2.8rem)', fontWeight: '700', marginTop: '0.4rem', marginBottom: '0.7rem', letterSpacing: '-0.3px' },
  sub: { color: '#6b7280', fontFamily: F, fontSize: '1.05rem', lineHeight: '1.6' },

  inner: { maxWidth: '1100px', margin: '0 auto', padding: '2.5rem 1.5rem 4rem' },

  introSection: { marginBottom: '4rem' },
  introGrid: { display: 'flex', gap: '2.5rem', flexWrap: 'wrap', alignItems: 'flex-start' },
  sectionHeading: { color: '#1a1a1a', fontFamily: F, fontSize: 'clamp(1.4rem,3vw,1.9rem)', fontWeight: '700', marginBottom: '1rem', letterSpacing: '-0.3px' },
  introText: { color: '#4b5563', fontFamily: F, fontSize: '0.97rem', lineHeight: '1.75', marginBottom: '0.85rem', flex: '1 1 300px' },

  hoursCard: { flex: '0 0 280px', backgroundColor: '#f8f9fa', borderRadius: '12px', padding: '1.75rem', border: '1px solid #e5e7eb' },
  hoursHeading: { color: '#1a1a1a', fontFamily: F, fontSize: '1rem', fontWeight: '700', marginBottom: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
  hoursIcon: { fontSize: '1.1rem' },
  hoursRow: { display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.65rem', flexWrap: 'wrap' },
  hoursDay: { color: '#374151', fontFamily: F, fontSize: '0.88rem', fontWeight: '500' },
  hoursTime: { color: '#1e8449', fontFamily: F, fontSize: '0.88rem', fontWeight: '600' },
  hoursNote: { color: '#9ca3af', fontFamily: F, fontSize: '0.78rem', marginTop: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '0.85rem' },

  menuSection: { borderTop: '1px solid #e5e7eb', paddingTop: '3.5rem', marginBottom: '4rem', textAlign: 'center' },
  eyebrowDark: { fontSize: '0.75rem', fontFamily: F, fontWeight: '600', letterSpacing: '1.8px', textTransform: 'uppercase', color: '#1e8449' },
  menuGrid: { display: 'flex', flexWrap: 'wrap', gap: '1.25rem', justifyContent: 'center', marginTop: '2.5rem' },
  menuCard: { flex: '1 1 240px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'left' },
  menuCardHeader: { display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.1rem', paddingBottom: '0.85rem', borderBottom: '1px solid #f3f4f6' },
  menuCatIcon: { fontSize: '1.5rem', lineHeight: 1 },
  menuCatTitle: { color: '#1a1a1a', fontFamily: F, fontSize: '1rem', fontWeight: '700' },
  menuItems: { display: 'flex', flexDirection: 'column', gap: '0.9rem' },
  menuItem: { display: 'flex', flexDirection: 'column', gap: '0.2rem' },
  menuItemTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem' },
  menuItemName: { color: '#1a1a1a', fontFamily: F, fontSize: '0.9rem', fontWeight: '600' },
  menuItemPrice: { color: '#1e8449', fontFamily: F, fontSize: '0.88rem', fontWeight: '700', whiteSpace: 'nowrap' },
  menuItemDesc: { color: '#6b7280', fontFamily: F, fontSize: '0.82rem', lineHeight: '1.4' },
  menuNote: { color: '#9ca3af', fontFamily: F, fontSize: '0.8rem', marginTop: '1.5rem' },

  ctaSection: { backgroundColor: '#f8f9fa', borderRadius: '16px', padding: '3rem 2rem', textAlign: 'center', border: '1px solid #e5e7eb' },
  ctaHeading: { color: '#1a1a1a', fontFamily: F, fontSize: '1.6rem', fontWeight: '700', marginBottom: '0.6rem', letterSpacing: '-0.3px' },
  ctaText: { color: '#6b7280', fontFamily: F, fontSize: '0.97rem', marginBottom: '1.75rem' },
  ctaButtons: { display: 'flex', gap: '0.85rem', justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtn: { display: 'inline-block', backgroundColor: '#1e8449', color: 'white', textDecoration: 'none', padding: '0.85rem 1.75rem', borderRadius: '8px', fontWeight: '600', fontFamily: F, fontSize: '0.95rem' },
  ctaBtnSecondary: { display: 'inline-block', backgroundColor: 'transparent', color: '#1e8449', textDecoration: 'none', padding: '0.82rem 1.75rem', borderRadius: '8px', fontWeight: '500', fontFamily: F, fontSize: '0.9rem', border: '1.5px solid #e5e7eb' },
};
